import React, { useState } from 'react';

interface RSVPData {
  name: string;
  name2: string;
  email: string;
  phone: string;
  countryCode: string;
  attending: boolean | null;
  dietaryRestrictions: string;
  favoriteMedia: string;
  additionalComments: string;
  guestCount: number;
  events: {
    sangeet: boolean;
    shaadi: boolean;
    reception: boolean;
  };
}

const RSVPPage: React.FC = () => {
  const [formData, setFormData] = useState<RSVPData>({
    name: '',
    name2: '',
    email: '',
    phone: '',
    countryCode: '+1',
    attending: null,
    dietaryRestrictions: '',
    favoriteMedia: '',
    additionalComments: '',
    guestCount: 1,
    events: {
      sangeet: false,
      shaadi: false,
      reception: false
    }
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const airtableApiKey = process.env.REACT_APP_AIRTABLE_API_KEY;
      const airtableBaseId = process.env.REACT_APP_AIRTABLE_BASE_ID;
      
      if (!airtableApiKey || !airtableBaseId) {
        throw new Error('Airtable configuration missing. Please add API key and Base ID to environment variables.');
      }

      const response = await fetch(`https://api.airtable.com/v0/${airtableBaseId}/RSVPs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${airtableApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fields: {
            'Name': formData.name,
            'Email': formData.email,
            'Attending': formData.attending ? 'Yes' : 'No',
            'Guest Count': formData.guestCount,
            ...(formData.name2 && { 'Partner Name': formData.name2 }),
            ...(formData.phone && { 'Phone': formData.countryCode + ' ' + formData.phone }),
            ...(formData.dietaryRestrictions && { 'Dietary Restrictions': formData.dietaryRestrictions }),
            ...(formData.favoriteMedia && { 'Favorite Song': formData.favoriteMedia }),
            ...(formData.additionalComments && { 'Additional Comments': formData.additionalComments }),
            ...(formData.attending && {
              'Sangeet': formData.events.sangeet ? 'Yes' : 'No',
              'Shaadi': formData.events.shaadi ? 'Yes' : 'No',
              'Reception': formData.events.reception ? 'Yes' : 'No'
            })
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Airtable API Error:', errorData);
        throw new Error(errorData?.error?.message || `HTTP ${response.status}: Failed to submit RSVP`);
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to submit RSVP. Please try again later.');
      console.error('Error submitting RSVP:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center" style={{ minHeight: '60vh', marginTop: '1cm' }}>
        <div className="clean-card p-12 text-center animate-slide-up">
          <div className="w-16 h-16 bg-neutral-900 rounded-full mx-auto mb-6 flex items-center justify-center">
            <span className="text-white text-2xl">✓</span>
          </div>
          <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
            Thank you for your RSVP!
          </h1>
          <p className="text-sage-grey-600 leading-relaxed mb-8">
            We've received your response and can't wait to celebrate with you!
          </p>
          <div className="section-divider"></div>
          <p className="text-neutral-500">
            See you in Khajuraho!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
          RSVP
        </h1>
        <div className="elegant-divider"></div>
        <p className="text-sage-grey-600">
          We can't wait to celebrate with you
        </p>

        {/* Photo */}
        <div className="photo-frame max-w-xl mx-auto mt-8 mb-8" style={{width: '85%', height: '400px', overflow: 'hidden', display: 'block', backgroundColor: 'transparent'}}>
          {/* Desktop/Tablet Image */}
          <img
            src="/images/rsvp/IMG_1538_optimized.jpg"
            alt="Shivangi and Ishan"
            className="w-full h-full object-cover hidden sm:block"
            style={{ objectFit: 'cover', height: '100%', maxWidth: 'none' }}
            loading="lazy"
            decoding="async"
          />
          {/* Mobile Image */}
          <img
            src="/images/rsvp/3D2A7618_optimized.jpg"
            alt="Shivangi and Ishan"
            className="w-full h-full object-cover block sm:hidden"
            style={{ objectPosition: 'bottom' }}
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="section-divider"></div>
      </div>

      <div className="glass-card p-12 text-center">
        <p className="text-soft-black text-lg leading-relaxed mb-6">
          RSVPs have now closed.
        </p>
        <p className="text-text-gray leading-relaxed">
          Please <a href="mailto:shivangiandishan@gmail.com" className="text-soft-black underline">contact us directly</a> for further information.
        </p>
      </div>

      {/* Hidden form for reference - RSVP closed */}
      <div className="hidden">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-md mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Your Full Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="minimal-input w-full"
                placeholder="Enter your full name"
                required
              />
            </div>
          </div>
          
          <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="minimal-input w-full"
                placeholder="your.email@example.com"
                required
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Phone Number
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.countryCode}
                  onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                  className="minimal-input w-20 flex-shrink-0 text-sm"
                >
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+675">🇵🇬 +675</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+1">🇨🇦 +1</option>
                  <option value="+971">🇦🇪 +971</option>
                  <option value="+852">🇭🇰 +852</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="minimal-input flex-1"
                  placeholder={formData.countryCode === 'other' ? '+XX 123-456-7890' : '(123) 456-7890'}
                />
              </div>
            </div>
          </div>
          
          <div className="glass-card p-8">
            <label className="block text-sm font-medium text-soft-black mb-6 uppercase tracking-wider">
              Will you be attending? *
            </label>
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer min-h-[60px]">
                <input
                  type="radio"
                  name="attending"
                  value="true"
                  checked={formData.attending === true}
                  onChange={() => setFormData({ ...formData, attending: true })}
                  className="sr-only"
                  required
                />
                <div className={`w-4 h-4 border mr-4 flex items-center justify-center ${formData.attending === true ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                  {formData.attending === true && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
                </div>
                <div>
                  <div className="font-medium text-soft-black tracking-wide">Yes, we'll be there</div>
                  <div className="text-sm text-text-gray">Can't wait to celebrate</div>
                </div>
              </label>
              
              <label className="flex items-center p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer min-h-[60px]">
                <input
                  type="radio"
                  name="attending"
                  value="false"
                  checked={formData.attending === false}
                  onChange={() => setFormData({ ...formData, attending: false })}
                  className="sr-only"
                  required
                />
                <div className={`w-4 h-4 border mr-4 flex items-center justify-center ${formData.attending === false ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                  {formData.attending === false && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
                </div>
                <div>
                  <div className="font-medium text-soft-black tracking-wide">Unfortunately, we can't make it</div>
                  <div className="text-sm text-text-gray">We'll miss you</div>
                </div>
              </label>
            </div>
          </div>
          
          {formData.attending === true && (
            <div className="animate-slide-up bg-light-gray p-8">
              <div className="mb-6">
                <h4 className="text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">Guest Information</h4>
                <p className="text-sm text-text-gray">If you have received a family invite, please enter the details of each person in your family! If there are more than two people you are trying to RSVP, you can refresh this page and submit two people at a time. Feel free to contact us on <a href="mailto:shivangiandishan@gmail.com" className="text-soft-black underline">shivangiandishan@gmail.com</a></p>
              </div>
              
              <label htmlFor="guestCount" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Number of Guests *
              </label>
              <select
                id="guestCount"
                value={formData.guestCount}
                onChange={(e) => setFormData({ ...formData, guestCount: parseInt(e.target.value) })}
                className="minimal-input w-full"
                required
              >
                <option value={1}>1 Guest (Just me)</option>
                <option value={2}>2 Guests (Me + Partner)</option>
              </select>
              
              {formData.guestCount === 2 && (
                <div className="mt-6">
                  <label htmlFor="name2" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                    Partner's Full Name *
                  </label>
                  <input
                    type="text"
                    id="name2"
                    value={formData.name2}
                    onChange={(e) => setFormData({ ...formData, name2: e.target.value })}
                    className="minimal-input w-full"
                    placeholder="Enter partner's name"
                    required
                  />
                </div>
              )}
            </div>
          )}
          
          {formData.attending === true && (
            <div className="glass-card p-8">
              <label className="block text-sm font-medium text-soft-black mb-6 uppercase tracking-wider">
                Which events will you attend? *
              </label>
              <div className="space-y-4">
                <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.events.sangeet}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      events: { ...formData.events, sangeet: e.target.checked }
                    })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.events.sangeet ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                    {formData.events.sangeet && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-soft-black tracking-wide">Sangeet</div>
                    <div className="text-sm text-text-gray">March 13th, 2026 - Music & Dance Celebration</div>
                  </div>
                </label>
                
                <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.events.shaadi}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      events: { ...formData.events, shaadi: e.target.checked }
                    })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.events.shaadi ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                    {formData.events.shaadi && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-soft-black tracking-wide">Shaadi</div>
                    <div className="text-sm text-text-gray">March 14th, 2026 - Wedding Ceremony</div>
                  </div>
                </label>
                
                <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.events.reception}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      events: { ...formData.events, reception: e.target.checked }
                    })}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.events.reception ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                    {formData.events.reception && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-soft-black tracking-wide">Reception</div>
                    <div className="text-sm text-text-gray">March 14th, 2026 - Evening Celebration</div>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {formData.attending === true && (
            <div>
              <label htmlFor="dietaryRestrictions" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Dietary Restrictions or Allergies (Optional)
              </label>
              <textarea
                id="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={(e) => setFormData({ ...formData, dietaryRestrictions: e.target.value })}
                className="minimal-input w-full"
                rows={3}
                placeholder="Please let us know about any dietary restrictions, allergies, or special needs"
              />
            </div>
          )}
          
          {formData.attending === true && (
            <div>
              <label htmlFor="favoriteMedia" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
                Favorite Song (Optional)
              </label>
              <input
                type="text"
                id="favoriteMedia"
                value={formData.favoriteMedia}
                onChange={(e) => setFormData({ ...formData, favoriteMedia: e.target.value })}
                className="minimal-input w-full"
                placeholder="Share a song you'd love to hear at the celebration"
              />
            </div>
          )}
          
          <div>
            <label htmlFor="additionalComments" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
              Additional Comments (Optional)
            </label>
            <textarea
              id="additionalComments"
              value={formData.additionalComments}
              onChange={(e) => setFormData({ ...formData, additionalComments: e.target.value })}
              className="minimal-input w-full"
              rows={3}
              placeholder="Any special requests, song dedications, or messages for the happy couple?"
            />
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="minimal-button w-full text-lg py-4 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin mr-2"></div>
                Submitting...
              </span>
            ) : (
              'Submit RSVP'
            )}
          </button>
        </form>
        
        <div className="mt-8 text-center text-xs text-text-gray uppercase tracking-wider">
          Please RSVP by December 15th, 2025.
        </div>
        <div className="mt-8 text-center text-xs text-text-gray uppercase tracking-wider">
          Once you RSVP, our events team will reach out closer to the wedding to help coordinate all the details.
        </div>
        <div className="mt-8 text-center text-xs text-text-gray uppercase tracking-wider">
          Other Questions? <a href="mailto:shivangiandishan@gmail.com" className="text-soft-black underline">Contact us directly</a>
        </div>
      </div>
    </div>
  );
};

export default RSVPPage;